import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "../../../../store/store";
import { AccordionRoutesEnum } from "../../../../types/AccordionRoutes";
import Accordion from "../../components/Accordion";
import Button from "../../components/Button";
import { ButtonBackgroundColorType } from "../../components/Button/Button";
import InputVF from "../../components/InputVF";
import { InputTypeEnum } from "../../components/InputVF/InputVF";
import SelectVF from "../../components/SelectVF";
import { UnemploymentInsuranceOptionModel } from "../../../../types/UnemploymentInsuranceOptionModel";
import { BackgroundColor, Container, Question, WrapperSelect} from "./InformationStep.style";
import CPRinput from "../../components/CPRinput";
import AdressInput from "../../components/AdressInput";
import ContactMeStep from "../ContactMeStep";
import { AddressData } from "../../components/AdressInput/AdressInput";
import { EmailValidationResponse } from "../../../../types/EmailValidationResponse";
import debounce from "lodash.debounce";
import { postEmailValidation } from "../../../../utils/api";
import { TrackingFlowTypeEnum } from "../../../../types/TrackingFlowTypeEnum";

interface FormData {
  [key: string]: { value: string; error: boolean };
}

const pensionSchemeOptions: UnemploymentInsuranceOptionModel[] = [
  { id: "PensionFromPreviousWorkplace", name: "Har en pensionsopsparing fra tidligere arbejdsplads" },
  { id: "PensionViaCurrentWorkplace", name: "Har en pensionsopsparing via min arbejdsplads" },
  { id: "NoPensionScheme", name: "Indbetaler ikke til en pensionsordning" },
  { id: "Other", name: "Andet (fx i form af selvkøbte aktier, obligationer, bygning eller virksomhed)" },
];

interface Props {
  step2: AccordionRoutesEnum;
}

export default function InformationStep({step2}: Props) {
  const setPayload = useStore.useSetPayload();
  const payload = useStore.usePayload();
  const translations = useStore.useTranslations();
  const setCompletedAccordionRoute = useStore.useSetCompletedAccordionRoute();
  const setAccordionRoute = useStore.useSetAccordionRoute();
  const [cprValidated, setcprValidated] = useState<boolean>();
  const [firstNameValidated, setFirstnameValidated] = useState<boolean>();
  const [lastNameValidated, setLastNameValidated] = useState<boolean>();
  const [mobilePhoneValidated, setMobilePhoneValidated] = useState<boolean>();
  const [emailValidated, setEmailValidated] = useState<boolean>();
  const [showContactMe, setShowContactMe] = useState(false);
  const [mobilePhone, setMobilePhone] = useState("");
  const [cpr, setCpr] = useState("");
  const [firstName, setFirstname] = useState("");
  const [lastName, setLastName] = useState("");
  const [mailInputInteraction, setMailInputInteraction] = useState("");
  const [emailValidationResponse, setEmailValidationResponse] = useState<EmailValidationResponse>({
    email: "",
    score: 0,
    suggestion: "",
    verdict: "",
  });
  const [cprErrorText, setCprErrorText] = useState("");

  const initialFormData = {
    socialSecurityNumber: {
      value: payload.socialSecurityNumber,
      error: !Boolean(payload.socialSecurityNumber),
    },
    firstName: {
      value: payload.firstName,
      error: !Boolean(payload.firstName),
    },
    lastName: {
      value: payload.lastName,
      error: !Boolean(payload.firstName),
    },
    mobilePhone: {
      value: payload.mobilePhone,
      error: !Boolean(payload.mobilePhone),
    },
    email: {
      value: payload.mailAddress,
      error: !Boolean(payload.mailAddress),
    },
    addressText: {
      value: payload.addressText ?? "",
      error: !Boolean(payload.addressText),
    },
    address: {
      value: payload.address ?? "",
      error: false,
    },
    zipCode: {
      value: payload.zipCode ?? "",
      error: false, //!Boolean(payload.zipCode),
    },
    city: {
      value: payload.city ?? "",
      error: false, //!Boolean(payload.city),
    },
    floor: {
      value: payload.floor ?? "",
      error: false, //!Boolean(payload.floor),
    },
    door: {
      value: payload.door ?? "",
      error: false, //!Boolean(payload.door),
    },
    houseNumber: {
      value: payload.houseNumber ?? "",
      error: false, //!Boolean(payload.houseNumber),
    },
    monthlySalary: {
      value: payload.monthlySalary !== undefined ? String(payload.monthlySalary) : "",
      error: (payload.wantsSalaryInsurance || payload.wantsSalaryInsuranceAndUnion) && !Boolean(payload.monthlySalary),
    },
    numberOfPeopleInHousehold: {
      value: payload.numberOfPeopleInHousehold !== undefined ? String(payload.numberOfPeopleInHousehold) : "",
      error: false,
    },
    numberOfChildrenInHousehold: {
      value: payload.numberOfChildrenInHousehold !== undefined ? String(payload.numberOfChildrenInHousehold) : "",
      error: false,
    },
    childrenBirthYears: {
      value: payload.childrenBirthYears ?? "",
      error: Boolean(payload.numberOfChildrenInHousehold) && !Boolean(payload.childrenBirthYears),
    },
    jobTitle: {
      value: payload.jobTitle ?? "",
      error: false,
    },
    pensionScheme: {
      value: payload.pensionScheme ?? "",
      error: false,
    },
  } as FormData;

  const [formData, setFormData] = useState(initialFormData);

  const setValue = (field: string, value: string, error: boolean) => {
    setFormData({
      ...formData,
      [field]: {
        ...formData[field],
        value,
        error,
      },
    });
  };

  const setSelectedAddress = useCallback(
    (addressData: AddressData) => {
        setFormData({
        ...formData,
        addressText: {
          value: addressData.addressText,
          error: !Boolean(addressData.addressText),
        },
        address: {
          value: addressData.address,
          error: false,
        },
        city: {
          value: addressData.city,
          error: false,// !Boolean(addressData.city),
        },
        door: {
          value: addressData.door,
          error: false,
        },
        floor: {
          value: addressData.floor,
          error: false,
        },
        houseNumber: {
          value: addressData.houseNumber,
          error: false, //!Boolean(addressData.houseNumber),
        },
        zipCode: {
          value: addressData.zipCode,
          error: false, //!Boolean(addressData.zipCode),
        },
      });
    },
    [formData]
  );

  //Validation
  //Valdition for CPR
  const cprHasError = (cpr: string) => {
    if (!cpr) {
      return true;
    }

    const inputWithRemovedDash = cpr.replace("-", "");
    if (inputWithRemovedDash.length !== 10) {
      setCprErrorText("CPR-nummer skal være på 10 cifre");
      return true;
    }

    if (!/^[0-9]+$/.test(inputWithRemovedDash)) {
      return true;
    }

    const day = parseInt(inputWithRemovedDash.substring(0, 2));

    if (day < 0 || day > 31) {
      return true;
    }

    const month = parseInt(inputWithRemovedDash.substring(2, 4));

    if (month < 0 || month > 12) {
      return true;
    }

    let year = parseInt(inputWithRemovedDash.substring(4, 6));

    if (year < 0 || year > 99) {
      return true;
    }

    if(!oldEnough(inputWithRemovedDash) && inputWithRemovedDash.length === 10) {
      // errorText = "Du skal være mindst 18 år gammel";
      setCprErrorText("Du kan melde dig ind i a-kassen, når du fylder 18 år");
      return true;
    }
    if(!isUserUnder65(inputWithRemovedDash) && inputWithRemovedDash.length === 10) {
      // errorText = "Du skal være under 65 år gammel";
      setCprErrorText("Du kan desværre ikke melde dig ind i a-kasse, når du er fyldt 65 år");
      return true;
    }

    return false;
  };

const oldEnough = (cpr: any) => {
  if (cpr == undefined) return false;
  cpr = cpr.toString();
  var cutoff = new Date();
  cutoff.setFullYear(
      (cutoff.getFullYear() - 18),
      cutoff.getMonth(),
      cutoff.getDate()
  );
  
  var y = cpr.substr(4,2);
  y = (parseInt(y,10) < 40) ? '20' + y : '19' + y;
  var birthday = new Date(y, (cpr.substr(2,2) - 1), cpr.substr(0,2));
  return (birthday <= cutoff);
}

const isUserUnder65 = (cpr: any) => {
    if (cpr == undefined) return false;
    cpr = cpr.toString();
    var y = cpr.substr(4,2);
    y = (parseInt(y,10) < 40) ? '20' + y : '19' + y;
    var birthday = new Date(y, (cpr.substr(2,2) - 1), cpr.substr(0,2));
    // birthdate should be a string in 'YYYY-MM-DD' format or a Date object
    const birth = typeof birthday === 'string' ? new Date(birthday) : birthday;
    if (isNaN(birth.getTime())) return false; // invalid date

    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
        age--;
    }
    return age < 65;
}

//Validation for firstname and lastname
const firstnameErrorCheck = (value: string) => {
  const reg = /\d$/;
  if (reg.test(value)) {
    return true
  } else if (!reg.test(value)) {
    return false
  }
}

const lastnameErrorCheck = (value: string) => {
  const reg = /\d$/;
  if (reg.test(value)) {
    return true
  } else if (!reg.test(value)) {
    return false
  }
}


//Validation for mobilePhone
const mobilePhoneErrorCheck = (value: string) => {
  const reg = /^\+?[0-9]{3}-?[0-9]{5,12}$/;
  if (!reg.test(value)) {
    return true
  } else {
    return false
  }
}


//Validation check for the input fields
const cprValidation = () => {
  if (cpr) {
    if (cprHasError(cpr) === false && cpr !== undefined) {
      setcprValidated(true);
    } else if (cprHasError(cpr) === true) {
      setcprValidated(false);
    }
  } 
};

const firstNameValidation = () => {
  if (firstName) {
    if (formData.firstName.error === true) {
      setFirstnameValidated(false) 
    } else if (formData.firstName.error === false) {
      setFirstnameValidated(true) 
    }
  } 
};

const lastNameValidation = () => {
  if(lastName) {
    if (formData.lastName.error === true) {
      setLastNameValidated(false)
    } else if (formData.lastName.error === false) {
      setLastNameValidated(true)
    }
  } 
};

const mobilePhoneValidation = () => {
  if (mobilePhone) {
    if(formData.mobilePhone.error === true) {
      setMobilePhoneValidated(false);
    } else if (formData.mobilePhone.error === false) {
      setMobilePhoneValidated(true);
    }
  } 
};

//Email validation
  useEffect(() => {
    setValue("email", emailValidationResponse.email, !emailValidationResponse.verdict || emailValidationResponse.verdict === "Invalid" ? true : false);
    //Keep the validated indicator in sync with the response itself, instead of a manual check that can run before the response arrives
    if (emailValidationResponse.verdict) {
      setEmailValidated(emailValidationResponse.verdict !== "Invalid");
    }
  }, [emailValidationResponse]);

  //monthlySalary is only required once the user has picked a product with salary insurance;
  //this step stays mounted the whole time so the requirement must be re-evaluated whenever the product choice changes, not just at mount.
  //The field is hidden once the payload already has a value (e.g. set on CircumstanceStep), so keep mirroring it here instead of only prefilling once.
  useEffect(() => {
    const monthlySalaryRequired = payload.wantsSalaryInsurance || payload.wantsSalaryInsuranceAndUnion;
    setFormData((prev) => {
      const value = payload.monthlySalary !== undefined ? String(payload.monthlySalary) : prev.monthlySalary.value;
      const shouldError = Boolean(monthlySalaryRequired) && !Boolean(value);
      if (prev.monthlySalary.error === shouldError && prev.monthlySalary.value === value) {
        return prev;
      }
      return { ...prev, monthlySalary: { ...prev.monthlySalary, value, error: shouldError } };
    });
  }, [payload.wantsSalaryInsurance, payload.wantsSalaryInsuranceAndUnion, payload.monthlySalary]);

  const validatingEmailResponse = async (email: string) => {
    if (!email) return;
    //if (email !== "") return;
    if(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)){
      setEmailValidationResponse({
        email: email,
        score: 0,
        suggestion: translations?.informationAboutYouStep.errorEmail!,
        verdict: "Invalid",
      });
      return;
    }
    try {
      const response = await postEmailValidation(email);
      setEmailValidationResponse(response.data);
    } catch (err) {
      //console.log(err);
    }
  };

  const debouncedEmailValidation = useRef(
    debounce(async (email: string) => {
      await validatingEmailResponse(email);
    }, 300)
  ).current;

  const emailValidation = async (email: string) => {
    debouncedEmailValidation(email);
  };

  //Blur can fire before the 300ms debounce resolves, leaving the last keystrokes unvalidated; cancel it and validate the current value immediately
  const emailValidationOnBlur = () => {
    debouncedEmailValidation.cancel();
    validatingEmailResponse(formData.email.value);
  };
  //Email validation end

  const nextStepAllowed = useMemo(() => {
    const formHasErrors = Object.values(formData)
      .map((v: any) => v.error)
      .includes(true);
      if (formHasErrors) {
        return false;
      } else if (!formHasErrors) {
        return true;
      } else {
        return false;
      }
  }, [formData, emailValidated, emailValidationResponse]); 

  const next = () => {
    if (payload.isDanishResident) {
      setAccordionRoute(AccordionRoutesEnum.ConsentStep, payload, step2 === AccordionRoutesEnum.ProductStepV2 ? TrackingFlowTypeEnum.VerticalV2 : TrackingFlowTypeEnum.Vertical);
    }
  };

  return (
    <>
      <Accordion
        title={translations?.informationAboutYouStep.title}
        step={AccordionRoutesEnum.InformationStep}
        onNext={() => {
          const informationData = {
            firstName: formData.firstName.value,
            lastName: formData.lastName.value,
            socialSecurityNumber: formData.socialSecurityNumber.value,
            address: formData.address.value,
            addressText: formData.addressText.value,
            city: formData.city.value ?? "",
            zipCode: formData.zipCode.value ?? "",
            houseNumber: formData.houseNumber.value ?? "",
            floor: formData.floor.value ?? "",
            door: formData.door.value ?? "",
            mobilePhone: formData.mobilePhone.value,
            mailAddress: formData.email.value,
            monthlySalary: formData.monthlySalary.value ? Number(formData.monthlySalary.value) : undefined,
            numberOfPeopleInHousehold: formData.numberOfPeopleInHousehold.value ? Number(formData.numberOfPeopleInHousehold.value) : undefined,
            numberOfChildrenInHousehold: formData.numberOfChildrenInHousehold.value !== "" ? Number(formData.numberOfChildrenInHousehold.value) : undefined,
            childrenBirthYears: formData.childrenBirthYears.value,
            jobTitle: formData.jobTitle.value,
            pensionScheme: formData.pensionScheme.value,
          };
          setCompletedAccordionRoute(AccordionRoutesEnum.InformationStep);
          setPayload(informationData);
          next();
          window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        }}
        stepComplete={nextStepAllowed}
        previousStep={AccordionRoutesEnum.CircumstanceStep}
        headerImg="/assets/images/id-card-h.svg"
        step2={step2}
      >
      <BackgroundColor>
        <CPRinput 
          id="informationStep_CPRinput" 
          name="infoStep_CPR" 
          labelText="CPR"
          extraText={translations?.informationAboutYouStep.cpr}
          placeholder="Angiv CPR-nummer"
          autocomplete="on"
          value={cpr} 
          step={AccordionRoutesEnum.InformationStep}
          onChange={(e) => {
            setcprValidated(undefined);
            setCpr(e.currentTarget.value);
            setValue("socialSecurityNumber", e.currentTarget.value, cprHasError(e.currentTarget.value) || !e.currentTarget.value);
          }}
          onBlur={() => {
            cprValidation();
          }} 
          validated={cprValidated}
          errorText={cprErrorText} />
        <InputVF
          id="informationStep_firstNames"
          type={InputTypeEnum.text}
          name="infoStep_firstname"
          labelText="Fornavne"
          placeholder="Angiv fornavne"
          autocomplete="given-name"
          value={firstName}
          step={AccordionRoutesEnum.InformationStep}
          errorText={translations?.informationAboutYouStep.errorFirstName}
          onChange={(e) => {
            setFirstnameValidated(undefined);
            setFirstname(e.currentTarget.value);
            setValue("firstName", e.currentTarget.value, firstnameErrorCheck(e.currentTarget.value) || !e.currentTarget.value);
          }}
          onBlur={() => { 
            firstNameValidation();
          }}
          validated={firstNameValidated}
        />
        <InputVF
          id="informationStep_lastName"
          type={InputTypeEnum.text}
          name="infoStep_lastname"
          labelText="Efternavn"
          placeholder="Angiv efternavn"
          autocomplete="family-name"
          value={lastName}
          step={AccordionRoutesEnum.InformationStep}
          errorText={translations?.informationAboutYouStep.errorMissingLastName}
          onChange={(e) => {
            setLastNameValidated(undefined);
            setLastName(e.currentTarget.value);
            setValue("lastName", e.currentTarget.value, lastnameErrorCheck(e.currentTarget.value) || !e.currentTarget.value);
          }}
          onBlur={() => { 
            lastNameValidation();
          }}
          validated={lastNameValidated}
        />
        <AdressInput 
          id="informationStep_adresse" 
          name="infoStep_adresse" 
          labelText="Adresse"
          placeholder="Angiv adresse" 
          autocomplete="street-address"
          step={AccordionRoutesEnum.InformationStep} 
          setSelectedAddress={setSelectedAddress} 
        />
        <InputVF
          id="informationStep_mobilePhone"
          type={InputTypeEnum.tel}
          name="infoStep_mobilePhone"
          labelText="Telefon"
          extraText={translations?.informationAboutYouStep.phone}
          placeholder="Angiv telefonnummer"
          autocomplete="tel"
          step={AccordionRoutesEnum.InformationStep}
          value={mobilePhone}
          errorText={translations?.informationAboutYouStep.errorPhone}
          onChange={(e) => {
            setMobilePhoneValidated(undefined);
            setMobilePhone(e.currentTarget.value);
            setValue("mobilePhone", e.currentTarget.value, mobilePhoneErrorCheck(e.currentTarget.value) || !e.currentTarget.value);
          }}
          onBlur={() => { 
            mobilePhoneValidation();
          }}
          validated={mobilePhoneValidated}
        />
        <InputVF
          id="informationStep_email"
          type={InputTypeEnum.email}
          name="infoStep_email"
          labelText="E-mail"
          extraText={translations?.informationAboutYouStep.mail}
          placeholder="Angiv e-mail"
          autocomplete="email"
          step={AccordionRoutesEnum.InformationStep}
          onChange={(e) => {
            setMailInputInteraction("1");
            setEmailValidated(undefined);
            setValue("email", e.currentTarget.value, true);
            emailValidation(e.currentTarget.value);
          }}
          onBlur={() => { 
            emailValidationOnBlur();
          }}
          validated={(formData.email.value  === "" && mailInputInteraction === "1") ? undefined : emailValidated}
          errorSuggestionText={translations?.informationAboutYouStep.errorEmail}
        />
         {payload.monthlySalary === undefined && (
           <InputVF
            id="informationStep_monthlySalary"
            type={InputTypeEnum.number}
            name="infoStep_monthlySalary"
            labelText="Månedsløn brutto"
            placeholder="Indtast nuværende brutto månedsløn"
            autocomplete="off"
            step={AccordionRoutesEnum.InformationStep}
            value={formData.monthlySalary.value}
            validated={undefined}
            onChange={(e) => {
              setValue("monthlySalary", e.currentTarget.value, (payload.wantsSalaryInsurance || payload.wantsSalaryInsuranceAndUnion) && !e.currentTarget.value);
            }}
          />
         )}
        <InputVF
          id="informationStep_numberOfPeopleInHousehold"
          type={InputTypeEnum.number}
          name="infoStep_numberOfPeopleInHousehold"
          labelText="Personer i husstanden"
          placeholder="Hvor mange?"
          autocomplete="off"
          step={AccordionRoutesEnum.InformationStep}
          value={formData.numberOfPeopleInHousehold.value}
          validated={undefined}
          onChange={(e) => {
            setValue("numberOfPeopleInHousehold", e.currentTarget.value, false);
          }}
        />
        <InputVF
          id="informationStep_numberOfChildrenInHousehold"
          type={InputTypeEnum.number}
          name="infoStep_numberOfChildrenInHousehold"
          labelText="Børn i husstanden"
          placeholder="Hvor mange?"
          autocomplete="off"
          step={AccordionRoutesEnum.InformationStep}
          value={formData.numberOfChildrenInHousehold.value}
          validated={undefined}
          onChange={(e) => {
            setValue("numberOfChildrenInHousehold", e.currentTarget.value, false);
          }}
        />
        {Boolean(Number(formData.numberOfChildrenInHousehold.value)) && (
          <InputVF
            id="informationStep_childrenBirthYears"
            type={InputTypeEnum.text}
            name="infoStep_childrenBirthYears"
            labelText="Hvornår er børnene født?"
            placeholder="Skriv årstal, adskilt af komma"
            autocomplete="off"
            step={AccordionRoutesEnum.InformationStep}
            value={formData.childrenBirthYears.value}
            validated={undefined}
            onChange={(e) => {
              setValue("childrenBirthYears", e.currentTarget.value, !e.currentTarget.value);
            }}
          />
        )}
        <InputVF
          id="informationStep_jobTitle"
          type={InputTypeEnum.text}
          name="infoStep_jobTitle"
          labelText="Stillingsbetegnelse"
          placeholder="Angiv din stillingsbetegnelse"
          autocomplete="off"
          step={AccordionRoutesEnum.InformationStep}
          value={formData.jobTitle.value}
          validated={undefined}
          onChange={(e) => {
            setValue("jobTitle", e.currentTarget.value, false);
          }}
        />
        <WrapperSelect>
          <SelectVF
            id="informationStep_pensionScheme"
            name="infoStep_pensionScheme"
            options={pensionSchemeOptions}
            placeholder="Vælg pensionsordning"
            label="Pensionsordning"
            selectedOption={formData.pensionScheme.value || undefined}
            step={AccordionRoutesEnum.InformationStep}
            onSelect={(id) => {
              setValue("pensionScheme", id, false);
            }}
          />
        </WrapperSelect>
       
        </BackgroundColor>
      </Accordion>

      <ContactMeStep showContactMe={showContactMe} onClose={() => setShowContactMe(false)} />
    </>
  );
}
